import { FC, useState, useEffect } from 'react'
import { Menu, X, Moon, Sun } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../../hooks/use-theme'

export const Navigation: FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()

  // Detect scroll for nav background change
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '#pricing' }
  ]

  return (
    <nav 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/90 dark:bg-neutral900/90 backdrop-blur-xl shadow-soft border-b border-neutral200/50 dark:border-neutral800/50' 
          : 'bg-transparent'
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer group" 
            onClick={() => navigate('/')}
          >
            <span className="text-2xl font-bold text-neutral900 dark:text-neutral100 transition-colors">
              Inventory
              <span className="text-sumoPrimary group-hover:text-sumoAmber transition-colors duration-200">
                Sumo
              </span>
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative text-neutral600 dark:text-neutral400 hover:text-neutral900 dark:hover:text-neutral100 transition-colors font-medium py-2 group"
              >
                {link.label}
                {/* Hover underline effect */}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-sumoPrimary transition-all duration-200 group-hover:w-full" />
              </a>
            ))}
            
            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="relative overflow-hidden group"
            >
              <div className="transition-transform duration-300 group-hover:scale-110">
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-neutral400 group-hover:text-sumoPrimary transition-colors" />
                ) : (
                  <Moon className="w-5 h-5 text-neutral600 group-hover:text-sumoPrimary transition-colors" />
                )}
              </div>
            </Button>

            {/* Sign In - Secondary */}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="text-neutral600 dark:text-neutral400 hover:text-neutral900 dark:hover:text-neutral100"
            >
              Sign In
            </Button>

            {/* Get Started - Primary CTA */}
            <Button 
              variant="primary" 
              onClick={() => navigate('/create-account')}
              className="btn-hover-lift rounded-xl px-6"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-neutral400" />
              ) : (
                <Moon className="w-5 h-5 text-neutral600" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="relative"
            >
              <div className={`transition-transform duration-200 ${mobileMenuOpen ? 'rotate-90' : ''}`}>
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </div>
            </Button>
          </div>
        </div>

        {/* Mobile Menu - Slide down animation */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
            mobileMenuOpen ? 'max-h-64 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pb-4 border-t border-neutral200 dark:border-neutral800 pt-4">
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-neutral700 dark:text-neutral300 hover:text-sumoPrimary transition-colors font-medium py-2"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 mt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate('/login')
                    setMobileMenuOpen(false)
                  }}
                  className="w-full rounded-xl"
                >
                  Sign In
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    navigate('/create-account')
                    setMobileMenuOpen(false)
                  }}
                  className="w-full rounded-xl"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}
